import React from 'react';
import { BriefcaseIcon, EnvelopeClosedIcon, CalendarIcon } from '@navikt/aksel-icons';
import type { TeamUsageDto } from '../../../../../../trustpulse-api/src/application/dto/organization/organization-usage-stats.dto';
import { DateFormatter } from '../utils/DateFormatter';
import classes from './TeamUsageCards.module.css';

export interface TeamUsageCardsTranslations {
  teamSize: string;
  channels: string;
  surveys: string;
  responses: string;
  responseRate: string;
  nextScheduled: string;
  scheduled: string;
  lastSurvey: string;
  noChannels: string;
  noSurveys: string;
  recurring: string;
  members: string;
  activity: string;
  engagement: string;
}

export interface TeamUsageCardsProps {
  teams: TeamUsageDto[];
  translations: TeamUsageCardsTranslations;
}

/**
 * Card-based view of team usage statistics for PDF export
 */
export const TeamUsageCards: React.FC<TeamUsageCardsProps> = ({ teams, translations }) => {
  const getResponseRateStatus = (rate: number): 'high' | 'medium' | 'low' => {
    if (rate >= 75) return 'high';
    if (rate >= 50) return 'medium';
    return 'low';
  };

  const formatScheduledInfo = (team: TeamUsageDto) => {
    if (team.recurringScheduledSurveysCount === 0) {
      return team.nextScheduledDate
        ? DateFormatter.formatShortDate(team.nextScheduledDate)
        : '-';
    }

    const frequencies = team.recurringFrequencies || [];
    return frequencies.length > 0
      ? `${translations.recurring}: ${frequencies.join(', ')}`
      : translations.recurring;
  };

  return (
    <div className={classes.cardsGrid}>
      {teams.map((team, index) => (
        <div key={index} className={classes.teamCard}>
          {/* Card Header */}
          <div className={classes.cardHeader}>
            <h3 className={classes.teamName}>{team.teamName}</h3>
            <div className={classes.teamMeta}>
              <span className={classes.metaItem}>
                <span className={classes.metaLabel}>{translations.teamSize}:</span>
                <span className={classes.metaValue}>{team.teamSize}</span>
              </span>
              <span className={classes.metaItem}>
                <span className={classes.metaLabel}>{translations.channels}:</span>
                <span className={classes.metaValue}>
                  {team.channels.length > 0 ? team.channels.join(', ') : translations.noChannels}
                </span>
              </span>
            </div>
          </div>

          {/* Card Body - Stats Grid */}
          <div className={classes.cardBody}>
            <div className={classes.statsRow}>
              <div className={classes.statBlock}>
                <div className={classes.statLabel}>
                  <BriefcaseIcon fontSize="1rem" aria-hidden />
                  <span>{translations.activity}</span>
                </div>
                <div className={classes.statGroup}>
                  <div className={classes.statItem}>
                    <span className={classes.statNumber}>{team.totalSurveysSent}</span>
                    <span className={classes.statUnit}>{translations.surveys}</span>
                  </div>
                  <div className={classes.statItem}>
                    <span className={classes.statNumber}>{team.totalResponses}</span>
                    <span className={classes.statUnit}>{translations.responses}</span>
                  </div>
                </div>
              </div>

              <div className={classes.statBlock}>
                <div className={classes.statLabel}>
                  <EnvelopeClosedIcon fontSize="1rem" aria-hidden />
                  <span>{translations.engagement}</span>
                </div>
                <div className={classes.responseRateDisplay}>
                  <div
                    className={classes.responseRateValue}
                    data-status={getResponseRateStatus(team.averageResponseRate)}
                  >
                    {team.averageResponseRate.toFixed(0)}%
                  </div>
                  <div className={classes.responseRateBar}>
                    <div
                      className={classes.responseRateFill}
                      data-status={getResponseRateStatus(team.averageResponseRate)}
                      style={{ width: `${team.averageResponseRate}%` }}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Card Footer - Scheduling Info */}
            <div className={classes.cardFooter}>
              <div className={classes.footerItem}>
                <div className={classes.footerLabel}>
                  <CalendarIcon fontSize="0.875rem" aria-hidden />
                  <span>{translations.nextScheduled}</span>
                </div>
                <span className={classes.footerValue}>{formatScheduledInfo(team)}</span>
              </div>
              {team.lastSurveyDate && (
                <div className={classes.footerItem}>
                  <div className={classes.footerLabel}>
                    <CalendarIcon fontSize="0.875rem" aria-hidden />
                    <span>{translations.lastSurvey}</span>
                  </div>
                  <span className={classes.footerValue}>
                    {DateFormatter.formatShortDate(team.lastSurveyDate)}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};
